import wx from "weixin-js-sdk";
import { $postWx } from "./create-http";

/**
 * 微信JS-SDK封装类
 * 参考official-web-ssr项目的实现方式
 */
class WeChatSDK {
  constructor() {
    this.isReady = false;
  }

  /**
   * 初始化微信SDK - 参考official-web-ssr的简化实现
   */
  init() {
    if (!this.isWeChatBrowser()) {
      return;
    }

    // 挂载wx对象到全局，与official-web-ssr保持一致
    window.wxApi = wx;

    const url = window.location.href.split("#")[0];

    $postWx("/wx_server/get_config_data.do", {
      url: url,
      wx_account_type: "TOWORDS",
    })
      .then((res) => {
        if (res.code == 200) {
          wx.config({
            debug: false,
            appId: res.result.appId,
            timestamp: res.result.timestamp,
            nonceStr: res.result.nonceStr,
            signature: res.result.signature,
            jsApiList: [
              "chooseImage",
              "chooseMessageFile",
              "getLocalImgData",
              "scanQRCode",
              "getLocation",
              "updateAppMessageShareData",
              "updateTimelineShareData",
              "navigateToMiniProgram",
            ],
          });

          wx.ready(() => {
            this.isReady = true;
            console.log("微信SDK初始化成功");
          });

          wx.error((res) => {
            console.error("微信SDK配置失败:", res);
            this.isReady = false;
          });
        } else {
          console.error("获取微信配置失败:", res);
        }
      })
      .catch((error) => {
        console.error("微信SDK初始化请求失败:", error);
      });
  }

  /**
   * 选择图片
   */
  chooseImage(options = {}) {
    if (!this.isReady) {
      return Promise.reject(new Error("微信SDK未就绪"));
    }

    return new Promise((resolve, reject) => {
      wx.chooseImage({
        count: options.count || 1,
        sizeType: options.sizeType || ["compressed"],
        sourceType: options.sourceType || ["album", "camera"],
        success: resolve,
        fail: reject,
      });
    });
  }

  /**
   * 从聊天记录选择文件
   */
  chooseMessageFile(options = {}) {
    if (!this.isReady) {
      return Promise.reject(new Error("微信SDK未就绪"));
    }

    return new Promise((resolve, reject) => {
      wx.chooseMessageFile({
        count: options.count || 1,
        type: options.type || ["image", "file"],
        success: resolve,
        fail: (error) => {
          console.error("微信chooseMessageFile失败:", error);
          reject(error);
        },
      });
    });
  }

  /**
   * 获取本地图片数据
   */
  getLocalImgData(localId) {
    if (!this.isReady) {
      return Promise.reject(new Error("微信SDK未就绪"));
    }

    return new Promise((resolve, reject) => {
      wx.getLocalImgData({
        localId: localId,
        success: resolve,
        fail: (error) => {
          console.error("微信getLocalImgData失败:", error);
          reject(error);
        },
      });
    });
  }

  /**
   * 扫码
   */
  scanQRCode(options = {}) {
    if (!this.isReady) {
      return Promise.reject(new Error("微信SDK未就绪"));
    }

    return new Promise((resolve, reject) => {
      wx.scanQRCode({
        needResult: options.needResult !== false ? 1 : 0,
        scanType: options.scanType || ["qrCode", "barCode"],
        success: resolve,
        fail: reject,
      });
    });
  }

  /**
   * 获取地理位置
   */
  getLocation(options = {}) {
    if (!this.isReady) {
      return Promise.reject(new Error("微信SDK未就绪"));
    }

    return new Promise((resolve, reject) => {
      wx.getLocation({
        type: options.type || "wgs84",
        success: resolve,
        fail: reject,
      });
    });
  }

  /**
   * 选择聊天文件并获取图片数据（组合方法）
   */
  async chooseAndReadMessageFile(options = {}) {
    if (!this.isReady) {
      throw new Error("微信SDK未就绪");
    }

    try {
      const result = await this.chooseMessageFile(options);

      if (result.localIds && result.localIds.length > 0) {
        const fileDataList = await Promise.all(
          result.localIds.map(async (localId) => {
            try {
              const imgData = await this.getLocalImgData(localId);
              return {
                localId,
                data: imgData.localData,
                name: `wechat_image_${Date.now()}.jpg`,
              };
            } catch (error) {
              console.error(`获取文件数据失败 ${localId}:`, error);
              return null;
            }
          })
        );

        return {
          ...result,
          fileDataList: fileDataList.filter((item) => item !== null),
        };
      }

      return result;
    } catch (error) {
      console.error("选择微信文件失败:", error);
      throw error;
    }
  }

  /**
   * 判断是否在微信环境中
   */
  isWeChatBrowser() {
    const ua = navigator.userAgent.toLowerCase();
    return (
      ua.includes("micromessenger") ||
      ua.includes("miniprogram") ||
      ua.includes("wxwork") ||
      window.__wxjs_environment === "miniprogram"
    );
  }

  /**
   * 判断是否在微信小程序环境中
   */
  isMiniProgram() {
    const ua = navigator.userAgent.toLowerCase();
    return (
      ua.includes("miniprogram") || window.__wxjs_environment === "miniprogram"
    );
  }

  /**
   * 跳转到微信小程序
   * @param {Object} options 跳转参数
   * @param {string} options.appId 小程序AppID
   * @param {string} options.path 小程序页面路径
   * @param {Object} options.extraData 传递给小程序的数据
   * @param {string} options.envVersion 小程序版本类型
   * @param {Function} options.success 成功回调
   * @param {Function} options.fail 失败回调
   */
  navigateToMiniProgram(options) {
    return new Promise((resolve, reject) => {
      if (!this.isWeChatBrowser()) {
        const error = new Error("不在微信环境中");
        if (options.fail) options.fail(error);
        reject(error);
        return;
      }

      if (!this.isReady) {
        const error = new Error("微信SDK未就绪");
        if (options.fail) options.fail(error);
        reject(error);
        return;
      }

      wx.navigateToMiniProgram({
        appId: options.appId,
        path: options.path || "",
        extraData: options.extraData || {},
        envVersion: options.envVersion || "release",
        success: (res) => {
          console.log("跳转小程序成功", res);
          if (options.success) options.success(res);
          resolve(res);
        },
        fail: (err) => {
          console.error("跳转小程序失败", err);
          if (options.fail) options.fail(err);
          reject(err);
        },
      });
    });
  }

  /**
   * 获取状态
   */
  getStatus() {
    return {
      isReady: this.isReady,
      isWeChatBrowser: this.isWeChatBrowser(),
      isMiniProgram: this.isMiniProgram(),
    };
  }
}

// 导出单例
export default new WeChatSDK();
